import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common";
import { StatusBadge } from "@/components/common";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate, money } from "@/lib/format";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "balance", label: "Balance outstanding", test: (b) => b.status !== "Paid" && b.remaining_balance > 0 },
  { id: "paid", label: "Paid", test: (b) => b.status === "Paid" },
  { id: "refunded", label: "Refunded", test: (b) => b.status === "Refunded" },
  { id: "all", label: "All", test: () => true },
];

const Summary = ({ label, value, tone, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="text-sm text-slate-500">{label}</div>
    <div className={cn("mt-1 font-mono text-2xl font-bold", tone)}>{value}</div>
  </div>
);

export default function PaymentsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("balance");
  const { data } = useFetch("/bookings", { scope: "all" });
  const all = useMemo(() => (data || []).filter((b) => b.status !== "Cancelled"), [data]);
  const rows = all.filter(TABS.find((t) => t.id === tab).test);
  const sum = (list, f) => list.reduce((s, b) => s + (Number(b[f]) || 0), 0);

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments" subtitle="Deposits, driver extras and outstanding balances per job. Mark a job as Paid from the booking." />
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Summary label="Total job value" value={money(sum(all, "total_estimate"))} tone="text-slate-900" testId="pay-total" />
        <Summary label="Deposits collected" value={money(sum(all, "deposit_amount"))} tone="text-emerald-700" testId="pay-deposits" />
        <Summary label="Driver extras" value={money(sum(all, "driver_extras_total"))} tone="text-sky-700" testId="pay-extras" />
        <Summary label="Outstanding balances" value={money(sum(all.filter(TABS[0].test), "remaining_balance"))} tone="text-rose-700" testId="pay-outstanding" />
      </div>
      <div className="mb-4 flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="payments-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`payments-tab-${t.id}`} className={cn("rounded-md px-3.5 py-1.5 text-sm font-medium", tab === t.id ? "bg-[#0E1E38] text-white" : "text-slate-600 hover:bg-slate-50")}>
            {t.label} <span className="ml-1 font-mono text-xs opacity-70">{all.filter(t.test).length}</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[860px] text-sm" data-testid="payments-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {["Date", "Move Pro #", "Customer", "Job price", "Driver extras", "Deposit", "Method", "Balance", "Status"].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="py-12 text-center text-slate-500" data-testid="payments-empty">Nothing here.</td></tr>}
            {rows.map((b) => (
              <tr key={b.id} onClick={() => navigate(`/bookings/${b.id}`)} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-sky-50/60" data-testid={`payment-row-${b.id}`}>
                <td className="whitespace-nowrap px-3 py-3">{fmtDate(b.moving_date, "d MMM yyyy")}</td>
                <td className="px-3 py-3 font-mono font-semibold">{b.job_number}</td>
                <td className="px-3 py-3 font-medium">{b.customer_name}</td>
                <td className="px-3 py-3 font-mono">{money(b.total_estimate)}</td>
                <td className="px-3 py-3 font-mono">{b.driver_extras_total ? money(b.driver_extras_total) : "—"}</td>
                <td className="px-3 py-3 font-mono text-emerald-700">{money(b.deposit_amount)}</td>
                <td className="px-3 py-3 text-slate-600">{b.deposit_method || "—"}</td>
                <td className="px-3 py-3 font-mono font-semibold">{money(b.remaining_balance)}</td>
                <td className="px-3 py-3"><StatusBadge status={b.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
